import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { api, ApiError, formatDate } from "../api";
import { useAuth } from "../auth";
import { Field, Spinner } from "../components/ui";

type Account = { name: string; email: string; role: string; emailReminders: boolean; createdAt: string };
const msgOf = (err: unknown) => (err instanceof ApiError ? err.message : "Não foi possível concluir.");

/** Minha conta: dados, lembretes, senha e direitos do titular (LGPD). */
export function AccountPage() {
  const { refresh, setUser, user } = useAuth();
  const navigate = useNavigate();
  const [acc, setAcc] = useState<Account | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [pwErr, setPwErr] = useState<Record<string, string>>({});
  const [del, setDel] = useState({ password: "", confirm: "" });
  const [delMsg, setDelMsg] = useState("");

  useEffect(() => {
    api
      .get<Account>("/account")
      .then(setAcc)
      .catch((err) => setMsg({ kind: "error", text: msgOf(err) }));
  }, []);
  if (!acc)
    return (
      <main className="page narrow">
        <Spinner />
      </main>
    );

  return (
    <main className="page narrow account">
      <div className="eyebrow">Minha conta</div>
      <h1>{acc.name}</h1>
      <p className="segment-meta">
        {acc.email} · conta criada em {formatDate(acc.createdAt)}
        {user?.code && (
          <>
            {" "}
            · seu código: <code>{user.code}</code>
          </>
        )}
      </p>

      <section className="card card-pad form">
        <h2 className="h-sm">Dados e preferências</h2>
        <Field label="Nome" value={acc.name} onChange={(e) => setAcc({ ...acc, name: e.target.value })} />
        <label className="check">
          <input
            type="checkbox"
            checked={acc.emailReminders}
            onChange={(e) => setAcc({ ...acc, emailReminders: e.target.checked })}
          />
          Receber lembretes de estudo por e-mail (flashcards pendentes e cursos parados)
        </label>
        {msg && <p className={`alert ${msg.kind}`}>{msg.text}</p>}
        <div className="actions">
          <button
            className="btn"
            onClick={async () => {
              try {
                await api.patch("/account", { name: acc.name, emailReminders: acc.emailReminders });
                await refresh();
                setMsg({ kind: "ok", text: "Dados salvos." });
              } catch (err) {
                setMsg({ kind: "error", text: msgOf(err) });
              }
            }}
          >
            Salvar
          </button>
        </div>
      </section>

      <section className="card card-pad form">
        <h2 className="h-sm">Trocar senha</h2>
        <div className="grid-2">
          <Field
            label="Senha atual"
            type="password"
            autoComplete="current-password"
            value={pw.current}
            error={pwErr.current}
            onChange={(e) => setPw({ ...pw, current: e.target.value })}
          />
          <Field
            label="Nova senha"
            type="password"
            autoComplete="new-password"
            value={pw.next}
            error={pwErr.next}
            hint="Mínimo de 8 caracteres, com letras e números."
            onChange={(e) => setPw({ ...pw, next: e.target.value })}
          />
        </div>
        <div className="actions">
          <button
            className="btn ghost"
            disabled={!pw.current || !pw.next}
            onClick={async () => {
              try {
                await api.post("/account/password", pw);
                setPw({ current: "", next: "" });
                setPwErr({});
                setMsg({ kind: "ok", text: "Senha alterada. As outras sessões foram encerradas." });
              } catch (err) {
                if (err instanceof ApiError) setPwErr(err.fields);
                setMsg({ kind: "error", text: msgOf(err) });
              }
            }}
          >
            Alterar senha
          </button>
        </div>
      </section>

      <section className="card card-pad">
        <h2 className="h-sm">Seus dados pessoais (LGPD)</h2>
        <p className="segment-meta">
          Você pode baixar uma cópia dos seus dados (cadastro, aceites, matrículas, progresso, respostas,
          anotações, comentários, perguntas ao assistente, certificados, medalhas e pagamentos) em formato
          JSON.
        </p>
        <a className="btn ghost" href="/api/account/export" download>
          Baixar meus dados
        </a>
      </section>

      <section className="card card-pad danger-zone">
        <h2 className="h-sm">Excluir conta</h2>
        <p className="segment-meta">
          A exclusão apaga definitivamente seu cadastro, progresso, anotações, comentários e certificados.
          Registros de pagamento são mantidos sem vínculo com o seu nome, pelo prazo exigido pela legislação
          fiscal.
        </p>
        <div className="grid-2">
          <Field
            label="Sua senha"
            type="password"
            autoComplete="current-password"
            value={del.password}
            onChange={(e) => setDel({ ...del, password: e.target.value })}
          />
          <Field
            label='Digite "EXCLUIR" para confirmar'
            value={del.confirm}
            onChange={(e) => setDel({ ...del, confirm: e.target.value })}
          />
        </div>
        {delMsg && <p className="alert error">{delMsg}</p>}
        <div className="actions">
          <button
            className="btn danger"
            disabled={!del.password || del.confirm !== "EXCLUIR"}
            onClick={async () => {
              try {
                await api.post("/account/delete", del);
                setUser(null);
                navigate("/entrar?conta=excluida");
              } catch (err) {
                setDelMsg(msgOf(err));
              }
            }}
          >
            Excluir minha conta
          </button>
        </div>
      </section>
    </main>
  );
}
